"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { cn } from "@drpcrd/utils/shared";
import { Form } from "../form";
import { InputTextField } from "../input-text-field";
import { InputTextArea } from "../input-text-area";
import { CountrySelect } from "../country-select";
import { CalloutBox } from "../callout-box";
import { SelectableCheckbox } from "../selectable-checkbox";
import { Typography } from "../typography";
import { ShieldCheckGradient } from "../icons/shield-check-gradient";
import type { OutreachResponseFooter } from "./types";

const schema = z.object({
  firstName: z.string().min(1, { error: "First name is required" }),
  lastName: z.string().min(1, { error: "Last name is required" }),
  address1: z.string().min(1, { error: "Address is required" }),
  address2: z.string().optional(),
  city: z.string().optional(),
  county: z.string().min(1, { error: "County / State is required" }),
  postCode: z.string().min(1, { error: "Post code is required" }),
  country: z.string().min(1, { error: "Country is required" }),
  comment: z.string().optional(),
  agreeToShare: z.boolean().refine((value) => value === true, {
    message: "You must agree to share your address",
  }),
});

export type GiftingAddressFormValues = z.infer<typeof schema>;

/** Prefill for the form — the creator's name and their stored address. */
export type GiftingAddressDefaults = {
  firstName?: string | null;
  lastName?: string | null;
  address?: {
    address?: string | null;
    address2?: string | null;
    city?: string | null;
    county?: string | null;
    postCode?: string | null;
    country?: string | null;
  } | null;
};

type Props = {
  defaults?: GiftingAddressDefaults;
  /** The data-sharing consent copy. Defaults read as the creator agreeing
   * for themselves; an agent responding on their behalf passes wording
   * that confirms the creator's agreement instead. */
  consent?: { title?: string; description?: string; label?: string };
  onSubmit: (data: GiftingAddressFormValues) => void;
  pending: boolean;
  footer: OutreachResponseFooter;
  /** See `OutreachNotInterestedForm`'s `className`. */
  className?: string;
};

/**
 * "Interested" response to a gifting mission — a shipping address instead
 * of a quote, plus consent to share it with the brand. Shared by the
 * creator app and the talent agency app; the caller supplies the submit
 * control through `footer`.
 */
export const OutreachGiftingForm = ({
  defaults,
  consent,
  onSubmit,
  pending,
  footer,
  className,
}: Props) => {
  const form = useForm<GiftingAddressFormValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues: {
      firstName: defaults?.firstName ?? "",
      lastName: defaults?.lastName ?? "",
      address1: defaults?.address?.address ?? "",
      address2: defaults?.address?.address2 ?? "",
      city: defaults?.address?.city ?? "",
      county: defaults?.address?.county ?? "",
      postCode: defaults?.address?.postCode ?? "",
      country: defaults?.address?.country ?? "GB",
      comment: "",
      agreeToShare: false,
    },
  });

  const agreeToShare = form.watch("agreeToShare");
  const submit = form.handleSubmit(onSubmit);

  return (
    <>
      <div className={cn("flex w-full flex-col", className)}>
        <Form {...form}>
          <form onSubmit={submit} className="mb-2 flex w-full flex-col gap-3">
            <InputTextField
              control={form.control}
              name="firstName"
              label="First name"
            />

            <InputTextField
              control={form.control}
              name="lastName"
              label="Last name"
            />

            <hr />

            <InputTextField
              control={form.control}
              name="address1"
              label="Address 1"
            />

            <InputTextField
              control={form.control}
              name="address2"
              label="Address 2"
              description="Optional"
            />

            <InputTextField
              control={form.control}
              name="city"
              label="Town / City"
              description="Optional"
            />

            <InputTextField
              control={form.control}
              name="county"
              label="County / State"
            />

            <InputTextField
              control={form.control}
              name="postCode"
              label="Post code / Zip code"
            />

            <CountrySelect control={form.control} name="country" />

            <hr />

            <InputTextArea
              control={form.control}
              placeholder="Type here"
              name="comment"
              label="Leave a comment"
            />
          </form>
        </Form>

        <CalloutBox
          className="mb-5"
          title={consent?.title ?? "Your data is important"}
          icon={<ShieldCheckGradient />}
          hideDivider
          footer={
            <button
              type="button"
              onClick={() =>
                form.setValue("agreeToShare", !form.getValues("agreeToShare"), {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              className="flex cursor-pointer items-start gap-1.5 py-1 text-left"
              disabled={pending}
            >
              <SelectableCheckbox
                isSelected={agreeToShare}
                className="mt-0.5 shrink-0"
              />
              <Typography
                size="sm"
                weight="semibold"
                className="text-primary-700"
              >
                {consent?.label ??
                  "I agree to share my address with the brand for gifting purposes only"}
              </Typography>
            </button>
          }
        >
          <Typography size="sm" className="text-primary-700 mt-1">
            {consent?.description ??
              "Your address will only be used to fulfil gifting and cannot be used or sold for any other marketing purposes."}
          </Typography>
        </CalloutBox>
      </div>

      {footer({ submit, disabled: pending || !agreeToShare, pending })}
    </>
  );
};
